import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";

export interface ActionItem {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface ToggleItem {
  label: string;
  icon: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Optional helper line shown under the label. */
  hint?: string;
}

interface ImageActionMenuProps {
  actions: ActionItem[];
  info?: { label: string; value: string }[];
  /** Switch-style entries (e.g. the AI watermark), shown above the actions. */
  toggles?: ToggleItem[];
  className?: string;
}

export function ImageActionMenu({ actions, info, toggles, className }: ImageActionMenuProps) {
  if (actions.length === 0 && (!info || info.length === 0) && (!toggles || toggles.length === 0)) return null;

  // Split destructive actions to put after separator
  const normal = actions.filter((a) => !a.destructive);
  const destructive = actions.filter((a) => a.destructive);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={className || "bg-background/80 hover:bg-background backdrop-blur-sm h-8 w-8"}
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        {info && info.length > 0 && (
          <>
            {info.map((item, i) => (
              <DropdownMenuLabel key={`info-${i}`} className="font-normal text-xs text-muted-foreground py-1">
                {item.label}: <span className="text-foreground">{item.value}</span>
              </DropdownMenuLabel>
            ))}
            {(normal.length > 0 || destructive.length > 0) && <DropdownMenuSeparator />}
          </>
        )}
        {toggles && toggles.length > 0 && (
          <>
            {toggles.map((t, i) => (
              <DropdownMenuCheckboxItem
                key={`t-${i}`}
                checked={t.checked}
                onCheckedChange={t.onCheckedChange}
                onSelect={(e) => e.preventDefault()}
                className="gap-2 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  {t.icon}
                  <span className="flex flex-col">
                    <span>{t.label}</span>
                    {t.hint && (
                      <span className="text-[10px] text-muted-foreground leading-tight">{t.hint}</span>
                    )}
                  </span>
                </span>
              </DropdownMenuCheckboxItem>
            ))}
            {(normal.length > 0 || destructive.length > 0) && <DropdownMenuSeparator />}
          </>
        )}
        {normal.map((action, i) => (
          <DropdownMenuItem
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className="gap-2 cursor-pointer"
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
        {destructive.length > 0 && normal.length > 0 && <DropdownMenuSeparator />}
        {destructive.map((action, i) => (
          <DropdownMenuItem
            key={`d-${i}`}
            onClick={action.onClick}
            disabled={action.disabled}
            className="gap-2 cursor-pointer text-destructive focus:text-destructive"
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
